import { ref, computed } from 'vue'
import { useAllowedFeatures } from '@/composables/app/useAllowedFeatures'
import { useVendors } from '@/composables/app/useVendors'
import { get as _get, pickBy as _pickBy } from 'lodash'

let consentInstance = {}

export const createConsent = {
  install: () => {
    const { allowedConsentManagementPlatform } = useAllowedFeatures()
    const { vendors: allowed_vendors, getVendor } = useVendors()

    const listeners = { cookieBarRequested: [] }

    const allowed_purposes = {
      functional: { purposes: [1, 9] },
      analytics: { purposes: [8, 9, 11] },
      marketing: { purposes: [2, 3, 4, 5, 6, 7, 10] }
    }

    const purposes = ref()
    const vendors = ref({})
    const tcf_purposes = ref()

    const hasFunctionalConsent = computed(() => purposes.value?.functional || allowed_purposes.functional.purposes.every(id => tcf_purposes.value?.[id]))
    const hasMarketingConsent = computed(() => purposes.value?.marketing || allowed_purposes.marketing.purposes.every(id => tcf_purposes.value?.[id]))

    const isVendorAllowed = name => {
      if (!name) return false

      const vendor = getVendor(name)

      if (!vendor) return hasMarketingConsent.value

      if (Object.keys(vendors.value).includes(vendor.name)) return vendors.value[vendor.name]
      if (purposes.value) return purposes.value[vendor.purpose]
      if (tcf_purposes.value) return !!vendor.purposes?.every(id => tcf_purposes.value[id])

      return hasMarketingConsent.value
    }

    const trigger = (event, ...args) => {
      if (!listeners[event]) return

      listeners[event].forEach(cb => {
        try {
          cb(...args)
        } catch (e) {
          throw e
        }
      })
    }

    const subscribe = (event, callback) => {
      if (!listeners[event]) return
      if (typeof callback !== 'function') return

      listeners[event].push(callback)
    }

    const setPurposes = data => {
      if (typeof data !== 'object') return

      purposes.value = _pickBy(data, (value, purposes) => Object.keys(allowed_purposes).includes(purposes) && typeof value === 'boolean')
    }

    const setVendors = data => {
      if (typeof data !== 'object') return

      vendors.value = _pickBy(data, (value, vendor) => allowed_vendors.find(({ name }) => name === vendor) && typeof value === 'boolean')
    }

    const setTcfData = data => {
      if (typeof data !== 'object') return

      const consents = _get(data, 'purpose.consents', {})

      tcf_purposes.value = _pickBy(consents, (value, key) => /^\d+$/.test(key) && typeof value === 'boolean')
    }

    consentInstance = {
      hasFunctionalConsent,
      hasMarketingConsent,
      trigger,
      subscribe,
      setPurposes,
      isVendorAllowed
    }

    if (allowedConsentManagementPlatform) {
      window.koalect = window.koalect || {}
      window.koalect.consent = {
        setPurposes,
        setVendors,
        setTcfData,
        subscribe
      }
    }
  }
}

export const useConsent = () => consentInstance